import React from 'react';
import { Button, ButtonGroup, Modal } from '@ver-uds/react';
import { Alert } from '@ver-uds/uswds-react';
import { AccountStatus } from 'eve-account-client';
import { useHistory } from 'react-router-dom';
import { useAuthState } from '@vis-auth/react';
import { EverifyUserRole } from '@vis-auth/vis-user-client';
import { EveRoutes } from '@ver-uds/e-verify';
import AlertConstants from '../constants/AlertConstants';
import ErrorConstants from '../constants/ErrorConstants';
import { InternalRoutes } from '../constants';
import { useAccountStatusUpdateMutation } from '../services';
import { scrollToTop } from '../utils/util';

export type CancelTerminateAccountModalProps = {
  open: boolean;
  close(): void;
  setErrorMessage(errorMessage: string | undefined): void;
  accountId: string;
};

const CancelTerminateAccountModal = ({
  open,
  close,
  setErrorMessage,
  accountId,
}: CancelTerminateAccountModalProps): JSX.Element => {
  const modalId = 'close-account-modal';
  const { user } = useAuthState();
  const hasGeneralUserRole = user?.roles?.includes(EverifyUserRole.GENERAL);

  const [mutate] = useAccountStatusUpdateMutation((error: Error | Response) => {
    setErrorMessage(ErrorConstants.GENERAL_ERROR);
  });

  const history = useHistory();

  const resetAndClose = (): void => {
    setErrorMessage('');
    close();
  };

  const onSubmit = async () => {
    const status = AccountStatus.ACTIVE;

    await mutate(
      {
        accountId,
        status,
      },
      {},
    );
  };

  const onFormSubmit = async () => {
    try {
      await onSubmit();
      resetAndClose();
      if (hasGeneralUserRole) {
        history.push(EveRoutes.COMPANY_CLIENTS_VIEW, { cancelTerminationSuccess: true });
      } else {
        history.push(InternalRoutes.PROFILE, { cancelTerminationSuccess: true });
      }
      scrollToTop();
    } catch (error) {
      setErrorMessage(ErrorConstants.GENERAL_ERROR);
      scrollToTop();
      close();
    }
  };

  return (
    <Modal id={modalId} header="Cancel Termination Request" open={open} close={resetAndClose} fixedToTop>
      <Modal.Content>
        <Alert status="warning">
          <Alert.Heading>{AlertConstants.CANCEL_TERMINATION_CONFIRM_HEADING}</Alert.Heading>
          <Alert.Text>{AlertConstants.CANCEL_TERMINATION_CONFIRM_TEXT}</Alert.Text>
        </Alert>
      </Modal.Content>
      <Modal.Footer>
        <ButtonGroup>
          <Button type="button" variant="secondary" onClick={resetAndClose}>
            Exit
          </Button>
          <Button type="button" variant="destructive" onClick={onFormSubmit}>
            Yes, Cancel Termination Request
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

// istanbul ignore if: Not used for test environment; Modal.setAppElement should be called in the tests directly.
if (process.env.NODE_ENV !== 'test') {
  Modal.setAppElement('#root');
}

export default CancelTerminateAccountModal;
